<template>
    <span>
        <span @click="startConfirming">
            <slot />
        </span>

        <Dialog :open="confirming" @update:open="closeModal">
            <DialogContent class="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>{{ title }}</DialogTitle>
                </DialogHeader>

                <p class="text-sm text-muted-foreground">
                    {{ content }}
                </p>

                <div v-if="allowPasskey && passkeysSupported" class="space-y-1 border-b border-border pb-4">
                    <Button variant="outline" class="w-full" :disabled="passkeyLoading" @click="verifyPasskey">
                        <KeyRound class="size-4" />
                        Confirm with passkey
                    </Button>
                    <p v-if="passkeyError" class="text-xs text-destructive">{{ passkeyError }}</p>
                </div>

                <div>
                    <Label for="confirm-password-input">Password</Label>
                    <Input
                        id="confirm-password-input"
                        v-model="password"
                        type="password"
                        placeholder="Enter your password"
                        class="mt-2"
                        @keydown.enter="confirmPassword"
                    />
                    <p v-if="error" class="mt-1 text-xs text-destructive">{{ error }}</p>
                </div>

                <DialogFooter class="mt-4">
                    <Button size="lg" variant="ghost" @click="closeModal">Cancel</Button>
                    <Button size="lg" :disabled="!password || processing" @click="confirmPassword">
                        {{ button }}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    </span>
</template>

<script lang="ts" setup>
import { ref, nextTick } from 'vue';
import axios from 'axios';
import { route } from 'ziggy-js';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { KeyRound } from 'lucide-vue-next';
import { usePasskeyVerification } from '@/composables/usePasskeyVerification';

const props = withDefaults(defineProps<{
    title?: string;
    content?: string;
    button?: string;
    forceConfirmation?: boolean;
    skipConfirmation?: boolean;
    /** Offer confirming with a passkey; `confirmed` then carries no password. Only for actions whose backend accepts a passkey confirmation. */
    allowPasskey?: boolean;
}>(), {
    title: 'Confirm Password',
    content: 'For your security, please confirm your password to continue.',
    button: 'Confirm',
    forceConfirmation: false,
    skipConfirmation: false,
    allowPasskey: false,
});

const emit = defineEmits<{
    confirmed: [password: string];
}>();

const confirming = ref(false);
const processing = ref(false);
const password = ref('');
const error = ref('');

const {
    verify: verifyPasskey,
    isLoading: passkeyLoading,
    isSupported: passkeysSupported,
    errorMessage: passkeyError,
} = usePasskeyVerification({
    routes: {
        options: route('passkey.confirm-options'),
        submit: route('passkey.confirm'),
    },
    onSuccess: () => {
        closeModal();
        nextTick(() => emit('confirmed', ''));
    },
});

const startConfirming = () => {
    if (props.forceConfirmation || props.skipConfirmation) {
        confirming.value = true;
        return;
    }

    axios.get(route('password.confirmation')).then((response) => {
        if (response.data.confirmed) {
            emit('confirmed', '');
        } else {
            confirming.value = true;
        }
    });
};

const confirmPassword = () => {
    if (!password.value) return;

    if (props.skipConfirmation) {
        const pw = password.value;
        closeModal();
        nextTick(() => emit('confirmed', pw));
        return;
    }

    processing.value = true;
    error.value = '';

    const pw = password.value;
    axios
        .post(route('password.confirm'), { password: pw })
        .then(() => {
            processing.value = false;
            closeModal();
            nextTick(() => emit('confirmed', pw));
        })
        .catch((err) => {
            processing.value = false;
            error.value = err.response?.data?.errors?.password?.[0] || 'Incorrect password';
        });
};

const closeModal = () => {
    confirming.value = false;
    password.value = '';
    error.value = '';
};
</script>
